import React from 'react';
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts';

import { Trend, TrendValue } from '../trend-value';

import { formatNumber } from '@/lib/formatNumber';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { cn } from '@/lib/utils';

export interface ChartRadialStackedProps {
  title: string;
  data: Record<string, number>[];
  chartConfig: ChartConfig;
  trend?: Trend;
  trendValue?: string;
  className?: string;
}

export function ChartRadialStacked({
  title,
  data,
  chartConfig,
  trend,
  trendValue,
  className,
}: ChartRadialStackedProps) {
  // Early return if data is invalid
  if (!data || data.length === 0) {
    return null;
  }

  const OUTER_RADIUS = 120;
  const INNER_RADIUS = 80;

  // return the sum
  const metricLabel = Object.values(data[0]).reduce((a, b) => (typeof b === 'number' ? a + b : a), 0);
  const formattedMetricLabel = formatNumber(metricLabel);

  // returns the data in sorted order by value
  const sortedData = Object.entries(data[0]).sort((a, b) => b[1] - a[1]); // Sort by value (descending)

  return (
    <Card className={cn('flex flex-col w-full min-w-60 p-4 shadow-none', className)}>
      <CardHeader className="p-0 space-y-0" data-name="header">
        <CardTitle className="uppercase font-inter heading-xxxs tracking-normal">{title}</CardTitle>
      </CardHeader>
      <CardContent data-name="content" className="p-0 flex-1 min-h-0">
        <ChartContainer config={chartConfig} className="mx-auto aspect-[2/1] w-full max-w-52">
          <RadialBarChart data={data} endAngle={180} innerRadius={INNER_RADIUS} outerRadius={OUTER_RADIUS} cy="100%">
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                        <tspan x={viewBox.cx} y={(viewBox.cy || 0) - 16} className="fill-foreground text-xl font-inter">
                          {formattedMetricLabel}
                        </tspan>
                      </text>
                    );
                  }

                  return null;
                }}
              />
            </PolarRadiusAxis>
            {sortedData.map(([key]) => (
              <RadialBar
                key={key}
                dataKey={key}
                stackId="a"
                cornerRadius={4}
                fill={chartConfig[key]?.color || `var(--color-${key})`}
                className="stroke-card stroke-2"
              />
            ))}
          </RadialBarChart>
        </ChartContainer>
        {trend && trendValue && (
          <TrendValue variant="long" trend={trend} trendValue={trendValue} className="mt-4 justify-center" />
        )}
      </CardContent>
    </Card>
  );
}
